"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  ThemeIcon,
  List,
  Divider,
  ColorSwatch,
  Image,
} from "@mantine/core";
import {
  IconPalette,
  IconPhoto,
  IconDroplet,
  IconEye,
  IconStar,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

export default function SeasonalColorAnalysisForNonBinaryContent() {
  const t = useTranslations("seasonalColorAnalysisForNonBinary");

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            {t("title")}
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            {t("subtitle")}
          </Text>

          {/* Non-Binary Color Analysis Image */}
          <Box mb="xl">
            <Image
              src="/images/generated/non-binary-color-analysis.jpg"
              alt="Inclusive Color Analysis for Non-Binary Individuals"
              width={800}
              height={600}
              radius="md"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        </Box>

        {/* Inclusive Approach */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("inclusiveApproachTitle")}
          </Title>
          <Text mb="lg">
            {t("inclusiveApproachIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="violet">
                {t("beyondGenderTitle")}
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>{t("beyondGenderList1")}</List.Item>
                <List.Item>{t("beyondGenderList2")}</List.Item>
                <List.Item>{t("beyondGenderList3")}</List.Item>
                <List.Item>{t("beyondGenderList4")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="violet">
                {t("personalizedRecommendationsTitle")}
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>{t("personalizedRecList1")}</List.Item>
                <List.Item>{t("personalizedRecList2")}</List.Item>
                <List.Item>{t("personalizedRecList3")}</List.Item>
                <List.Item>{t("personalizedRecList4")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Understanding Color Analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("understandingColorAnalysisTitle")}
          </Title>
          <Text mb="lg">
            {t("understandingIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <ThemeIcon size={60} radius="xl" color="blue" variant="light" mb="md">
                <IconDroplet size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("skinUndertoneTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("skinUndertoneDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("skinUndertoneWarm")}</List.Item>
                <List.Item>{t("skinUndertoneCool")}</List.Item>
                <List.Item>{t("skinUndertoneNeutral")}</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="violet" variant="light" mb="md">
                <IconEye size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("eyeColorTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("eyeColorDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("eyeColorList1")}</List.Item>
                <List.Item>{t("eyeColorList2")}</List.Item>
                <List.Item>{t("eyeColorList3")}</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="orange" variant="light" mb="md">
                <IconStar size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                {t("hairColorTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("hairColorDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("hairColorList1")}</List.Item>
                <List.Item>{t("hairColorList2")}</List.Item>
                <List.Item>{t("hairColorList3")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Color Seasons for Non-Binary Individuals */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("colorSeasonsTitle")}
          </Title>
          <Text mb="lg">
            {t("colorSeasonsIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                {t("springSummerTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("springSummerDescription")}
              </Text>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("springSummerList1")}</List.Item>
                <List.Item>{t("springSummerList2")}</List.Item>
                <List.Item>{t("springSummerList3")}</List.Item>
                <List.Item>{t("springSummerList4")}</List.Item>
              </List>
              <Group gap="xs">
                <ColorSwatch color="#FF6B6B" size={20} />
                <ColorSwatch color="#4ECDC4" size={20} />
                <ColorSwatch color="#87CEEB" size={20} />
                <ColorSwatch color="#DDA0DD" size={20} />
                <ColorSwatch color="#F0E68C" size={20} />
              </Group>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                {t("autumnWinterTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("autumnWinterDescription")}
              </Text>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("autumnWinterList1")}</List.Item>
                <List.Item>{t("autumnWinterList2")}</List.Item>
                <List.Item>{t("autumnWinterList3")}</List.Item>
                <List.Item>{t("autumnWinterList4")}</List.Item>
              </List>
              <Group gap="xs">
                <ColorSwatch color="#8B4513" size={20} />
                <ColorSwatch color="#228B22" size={20} />
                <ColorSwatch color="#000080" size={20} />
                <ColorSwatch color="#8B008B" size={20} />
                <ColorSwatch color="#DC143C" size={20} />
              </Group>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Style Expression */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("styleExpressionTitle")}
          </Title>
          <Text mb="lg">
            {t("styleExpressionIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                {t("androgynousTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("androgynousDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("androgynousList1")}</List.Item>
                <List.Item>{t("androgynousList2")}</List.Item>
                <List.Item>{t("androgynousList3")}</List.Item>
                <List.Item>{t("androgynousList4")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="pink">
                {t("feminineTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("feminineDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("feminineList1")}</List.Item>
                <List.Item>{t("feminineList2")}</List.Item>
                <List.Item>{t("feminineList3")}</List.Item>
                <List.Item>{t("feminineList4")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="gray">
                {t("masculineTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("masculineDescription")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>{t("masculineList1")}</List.Item>
                <List.Item>{t("masculineList2")}</List.Item>
                <List.Item>{t("masculineList3")}</List.Item>
                <List.Item>{t("masculineList4")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Makeup and Grooming */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("makeupGroomingTitle")}
          </Title>
          <Text mb="lg">
            {t("makeupGroomingIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="violet">
                {t("makeupRecommendationsTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("makeupDescription")}
              </Text>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("makeupList1")}</List.Item>
                <List.Item>{t("makeupList2")}</List.Item>
                <List.Item>{t("makeupList3")}</List.Item>
                <List.Item>{t("makeupList4")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="teal">
                {t("groomingStylingTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {t("groomingDescription")}
              </Text>
              <List size="sm" spacing="xs" mb="md">
                <List.Item>{t("groomingList1")}</List.Item>
                <List.Item>{t("groomingList2")}</List.Item>
                <List.Item>{t("groomingList3")}</List.Item>
                <List.Item>{t("groomingList4")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Common Questions */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("commonQuestionsTitle")}
          </Title>
          <Stack gap="md">
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq1Question")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq1Answer")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq2Question")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq2Answer")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq3Question")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq3Answer")}
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                {t("faq4Question")}
              </Title>
              <Text size="sm" c="dimmed">
                {t("faq4Answer")}
              </Text>
            </Box>
          </Stack>
        </Card>

        {/* CTA */}
        <Box ta="center" py="xl">
          <Title order={2} mb="md">
            {t("ctaTitle")}
          </Title>
          <Text size="lg" c="dimmed" mb="xl">
            {t("ctaDescription")}
          </Text>
          <Group justify="center" gap="md">
            <Button
              size="xl"
              component={Link}
              href="/color-analysis-quiz"
              color="violet"
              rightSection={<IconPalette size={20} />}
            >
              {t("quizButton")}
            </Button>
            <Button
              size="xl"
              variant="outline"
              component={Link}
              href="/ai-color-analysis"
              color="violet"
              rightSection={<IconPhoto size={20} />}
            >
              {t("photoAnalysisButton")}
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
